import { useQuery } from '@tanstack/react-query';
import { Camera, CameraOff } from 'lucide-react';
import { useEffect } from 'react';
import { api, toApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

interface PunchThumbnailProps {
  recordId: string;
  kind?: 'TIME_IN' | 'TIME_OUT';
  className?: string;
  onClick?: () => void;
  alt: string;
}

/**
 * A punch photo, small.
 *
 * Fetched through the API client rather than set as an `<img src>`: the route
 * requires the session, and a bare src would be sent without it and come back
 * 401. The object URL is revoked on unmount so scrolling a long register does
 * not accumulate decoded images in memory.
 */
export function PunchThumbnail({
  recordId,
  kind = 'TIME_IN',
  className,
  onClick,
  alt,
}: PunchThumbnailProps) {
  const {
    data: url,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['punch-photo', recordId, kind],
    gcTime: 0,
    staleTime: 0,
    queryFn: async () => {
      const response = await api.get<Blob>(`/attendance/photos/${recordId}/${kind}`, {
        responseType: 'blob',
      });
      return URL.createObjectURL(response.data);
    },
  });

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  const base = cn(
    'size-10 shrink-0 overflow-hidden rounded-md border bg-muted',
    onClick && 'cursor-pointer hover:ring-2 hover:ring-ring',
    className,
  );

  if (isLoading) {
    return (
      <div className={cn(base, 'grid animate-pulse place-items-center')} aria-hidden="true">
        <Camera className="size-4 text-muted-foreground" />
      </div>
    );
  }

  /*
   * Three outcomes, not two.
   *
   * A punch with no photo and a photo that failed to load used to render the
   * same silent camera icon as the loading state, marked `aria-hidden` so a
   * screen reader got nothing at all. Since clocking out stopped requiring a
   * photo, "there isn't one" is the common case and a perfectly good answer —
   * but it should not be spelled the same way as "the server refused", least
   * of all on a register somebody is checking to decide whether a punch is
   * genuine.
   */
  if (isError || !url) {
    const missing = toApiError(error).statusCode === 404;
    const message = missing ? 'No photo for this punch' : 'Photo could not be loaded';

    return (
      <div className={cn(base, 'grid place-items-center')} title={message}>
        {missing ? (
          <Camera className="size-4 text-muted-foreground" aria-hidden="true" />
        ) : (
          <CameraOff className="size-4 text-destructive/70" aria-hidden="true" />
        )}
        <span className="sr-only">{message}</span>
      </div>
    );
  }

  return onClick ? (
    <button type="button" className={base} onClick={onClick} title="View punch photo">
      <img src={url} alt={alt} className="size-full object-cover" />
    </button>
  ) : (
    <div className={base}>
      <img src={url} alt={alt} className="size-full object-cover" />
    </div>
  );
}
